import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllTaskTracking } from "../redux/actions/taskTrackingActions";
import {
  FiRefreshCw,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiNavigation,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";

const API_KEY = process.env.REACT_APP_MAP_API_KEY;

/* ------------------------------------------------------------------ */
/*  Google Maps loader                                                */
/* ------------------------------------------------------------------ */
let googleMapsPromise = null;
const loadGoogleMaps = (apiKey) => {
  if (window.google && window.google.maps) return Promise.resolve(window.google.maps);
  if (googleMapsPromise) return googleMapsPromise;
  googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
  return googleMapsPromise;
};

/* ------------------------------------------------------------------ */
/*  Reverse geocode with cache                                        */
/* ------------------------------------------------------------------ */
const addressCache = new Map();      // "lat,lng" -> address string
const pendingRequests = new Map();   // "lat,lng" -> Promise

const reverseGeocode = async (lat, lng) => {
  const key = `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
  if (addressCache.has(key)) return addressCache.get(key);
  if (pendingRequests.has(key)) return pendingRequests.get(key);

  const p = fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${API_KEY}`
  )
    .then((r) => r.json())
    .then((data) => {
      const address =
        data.status === "OK"
          ? data.results[0]?.formatted_address || "Address not found"
          : "Unable to fetch address";
      addressCache.set(key, address);
      pendingRequests.delete(key);
      return address;
    })
    .catch(() => {
      const fallback = "Error fetching address";
      addressCache.set(key, fallback);
      pendingRequests.delete(key);
      return fallback;
    });

  pendingRequests.set(key, p);
  return p;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */
const formatIndianDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  const offset = date.getTimezoneOffset() * 60000;
  const istDate = new Date(date.getTime() - offset);
  const day = String(istDate.getDate()).padStart(2, "0");
  const month = String(istDate.getMonth() + 1).padStart(2, "0");
  const year = istDate.getFullYear();
  let hours = istDate.getHours();
  const minutes = String(istDate.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  hours = String(hours).padStart(2, "0");
  return `${day}-${month}-${year}, ${hours}:${minutes} ${ampm}`;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "-";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const designationColor = (d = "") => {
  const s = d.toLowerCase();
  if (s.includes("cmm")) return { bg: "#ede9fe", fg: "#5b21b6" };
  if (s.includes("co")) return { bg: "#dbeafe", fg: "#1e40af" };
  if (s.includes("engineer")) return { bg: "#dcfce7", fg: "#166534" };
  return { bg: "#f1f5f9", fg: "#334155" };
};

/* Custom SVG pin with number */
const makeMarkerIcon = (maps, number, active) => {
  const fill = active ? "#ef4444" : "#2563eb";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="48" viewBox="0 0 38 48">
    <defs>
      <filter id="s" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/>
      </filter>
    </defs>
    <path filter="url(#s)" d="M19 1C9.6 1 2 8.6 2 18c0 12.4 17 28 17 28s17-15.6 17-28C36 8.6 28.4 1 19 1z"
      fill="${fill}" stroke="#ffffff" stroke-width="2.5"/>
    <text x="19" y="24" font-family="Inter, Arial, sans-serif" font-size="13"
      font-weight="700" fill="#ffffff" text-anchor="middle">${number}</text>
  </svg>`;
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new maps.Size(38, 48),
    anchor: new maps.Point(19, 48),
  };
};

/* ------------------------------------------------------------------ */
/*  Map component                                                     */
/* ------------------------------------------------------------------ */
const MapView = ({ markers, activeIndex, onMarkerClick }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const markerObjsRef = useRef([]);
  const infoWindowRef = useRef(null);
  const markersKey = useMemo(
    () => JSON.stringify(markers.map((m) => [m.lat, m.lng])),
    [markers]
  );

  /* init map */
  useEffect(() => {
    let cancelled = false;
    if (!API_KEY) return;
    loadGoogleMaps(API_KEY).then((maps) => {
      if (cancelled || !containerRef.current || mapRef.current) return;
      mapsRef.current = maps;
      mapRef.current = new maps.Map(containerRef.current, {
        center: { lat: 22.5726, lng: 88.3639 },
        zoom: 9,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        styles: [
          { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
        ],
      });
      infoWindowRef.current = new maps.InfoWindow();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* rebuild markers */
  useEffect(() => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map) return;

    markerObjsRef.current.forEach((m) => m.setMap(null));
    markerObjsRef.current = [];

    if (!markers.length) return;

    const bounds = new maps.LatLngBounds();

    markers.forEach((m, idx) => {
      const marker = new maps.Marker({
        position: { lat: m.lat, lng: m.lng },
        map,
        title: m.title || "",
        icon: makeMarkerIcon(maps, idx + 1, idx === activeIndex),
        zIndex: idx === activeIndex ? 999 : idx + 1,
      });
      marker.addListener("click", () => onMarkerClick && onMarkerClick(idx));
      markerObjsRef.current[idx] = marker;
      bounds.extend({ lat: m.lat, lng: m.lng });
    });

    if (markers.length > 1) map.fitBounds(bounds, 80);
    else {
      map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
      map.setZoom(15);
    }
    // eslint-disable-next-line
  }, [markersKey]);

  /* active marker highlight + focus + info window */
  useEffect(() => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map) return;

    markerObjsRef.current.forEach((mk, idx) => {
      if (!mk) return;
      mk.setIcon(makeMarkerIcon(maps, idx + 1, idx === activeIndex));
      mk.setZIndex(idx === activeIndex ? 999 : idx + 1);
    });

    if (activeIndex >= 0 && markers[activeIndex]) {
      const pos = { lat: markers[activeIndex].lat, lng: markers[activeIndex].lng };
      map.panTo(pos);
      if (map.getZoom() < 14) map.setZoom(15);
      if (infoWindowRef.current) {
        infoWindowRef.current.setContent(markers[activeIndex].info || "");
        infoWindowRef.current.open(map, markerObjsRef.current[activeIndex]);
      }
    }
    // eslint-disable-next-line
  }, [activeIndex, markers]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
};

/* ------------------------------------------------------------------ */
/*  Main page                                                         */
/* ------------------------------------------------------------------ */
const TrackYourAgent = () => {
  const dispatch = useDispatch();
  const { taskTrackingData = [], loading, error } = useSelector(
    (state) => state.taskTracking
  );

  const [activeIndex, setActiveIndex] = useState(-1);
  const [search, setSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [, forceTick] = useState(0);
  const [addresses, setAddresses] = useState({}); // idx -> address string
  const listRefs = useRef([]);

  const fetchData = () => {
    dispatch(
      fetchAllTaskTracking({
        page: 1,
        limit: 50,
        district: "",
        municipality: "",
        search: "",
        fromDate: "",
        toDate: "",
        status: "ongoing",
        designation: "",
      })
    );
    setLastUpdated(Date.now());
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [dispatch]);

  /* auto-refresh every 60s */
  useEffect(() => {
    const id = setInterval(() => fetchData(), 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  /* tick for "x ago" label */
  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  /* build markers (lat/lng only) */
  const markers = useMemo(() => {
    return taskTrackingData
      .filter((i) => i.start_latitude && i.start_longitude)
      .map((i) => ({
        lat: Number(i.start_latitude),
        lng: Number(i.start_longitude),
        title: i.user_name || "",
        raw: i,
      }));
  }, [taskTrackingData]);

  /* reverse-geocode every marker */
  useEffect(() => {
    if (!markers.length || !API_KEY) return;
    let cancelled = false;

    (async () => {
      const results = {};
      const batchSize = 5;
      for (let i = 0; i < markers.length; i += batchSize) {
        if (cancelled) return;
        const batch = markers.slice(i, i + batchSize);
        const addrs = await Promise.all(batch.map((m) => reverseGeocode(m.lat, m.lng)));
        addrs.forEach((addr, j) => {
          results[i + j] = addr;
        });
        if (!cancelled) setAddresses({ ...results });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [markers]);

  /* markers with info window built AFTER address resolution */
  const markersWithInfo = useMemo(() => {
    return markers.map((m, idx) => {
      const r = m.raw;
      const addr = addresses[idx] || "Fetching address…";
      return {
        ...m,
        address: addr,
        info: `
          <div style="font-family:Inter,Arial,sans-serif;font-size:13px;line-height:1.6;min-width:240px;max-width:320px;padding:4px 2px">
            <div style="font-weight:700;font-size:14px;margin-bottom:4px">${r.user_name || "-"}</div>
            <div style="color:#475569;margin-bottom:6px">${r.task_name || "-"}</div>
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px">
              <span style="background:#dbeafe;color:#1e40af;padding:1px 8px;border-radius:999px;font-size:11px;font-weight:600">${r.designation || "-"}</span>
              <span style="background:#dcfce7;color:#166534;padding:1px 8px;border-radius:999px;font-size:11px;font-weight:600">${r.status || "-"}</span>
            </div>
            <div style="color:#334155;margin-bottom:4px">📞 ${r.user_phone || "-"}</div>
            <div style="color:#334155;display:flex;gap:6px;align-items:flex-start">
              <span style="flex-shrink:0">📍</span>
              <span>${addr}</span>
            </div>
            <div style="color:#94a3b8;font-size:11px;margin-top:6px">${formatIndianDate(r.created_at)}</div>
          </div>`,
      };
    });
  }, [markers, addresses]);

  /* filtered list for sidebar (address also searchable) */
  const visibleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return markersWithInfo
      .map((m, idx) => ({ ...m, idx }))
      .filter((m) => {
        if (!q) return true;
        const r = m.raw;
        return (
          (r.user_name || "").toLowerCase().includes(q) ||
          (r.user_phone || "").toLowerCase().includes(q) ||
          (r.task_name || "").toLowerCase().includes(q) ||
          (m.address || "").toLowerCase().includes(q)
        );
      });
  }, [markersWithInfo, search]);

  const handleSelect = (idx) => {
    setActiveIndex(idx);
    const el = listRefs.current[idx];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f8fafc",
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      {/* ---------- Header ---------- */}
      <header
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "#fff",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          boxShadow: "0 2px 12px rgba(15,23,42,0.15)",
          zIndex: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(59,130,246,0.45)",
            }}
          >
            <FiMapPin size={20} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}>
              Track Your Agent
            </h1>
            <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
              Live field activity · West Bengal
            </p>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginLeft: 12,
              padding: "5px 12px",
              background: "rgba(34,197,94,0.12)",
              border: "1px solid rgba(34,197,94,0.35)",
              borderRadius: 999,
            }}
          >
            <span style={{ position: "relative", display: "inline-flex", width: 8, height: 8 }}>
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background: "#22c55e",
                  animation: "pulseRing 1.8s ease-out infinite",
                }}
              />
              <span
                style={{
                  position: "relative",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#22c55e",
                }}
              />
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#4ade80" }}>
              {markers.length} Active
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "#94a3b8" }}>
            Updated {lastUpdated ? timeAgo(new Date(lastUpdated).toISOString()) : "-"}
          </span>
          <button
            onClick={fetchData}
            disabled={loading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 10,
              border: "1px solid rgba(148,163,184,0.25)",
              background: "rgba(255,255,255,0.06)",
              color: "#e2e8f0",
              fontSize: 13,
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <FiRefreshCw
              size={14}
              style={{ animation: loading ? "spin 1s linear infinite" : "none" }}
            />
            Refresh
          </button>
          <button
            onClick={() => setSidebarOpen((s) => !s)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 10,
              border: "1px solid rgba(148,163,184,0.25)",
              background: "rgba(255,255,255,0.06)",
              color: "#e2e8f0",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {sidebarOpen ? <FiChevronLeft size={14} /> : <FiChevronRight size={14} />}
            {sidebarOpen ? "Hide List" : "Show List"}
          </button>
        </div>
      </header>

      {/* ---------- Body ---------- */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
        {/* Sidebar */}
        <aside
          style={{
            width: sidebarOpen ? 350 : 0,
            minWidth: sidebarOpen ? 350 : 0,
            transition: "all 0.25s ease",
            background: "#fff",
            borderRight: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: 14, borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ position: "relative" }}>
              <FiSearch
                size={16}
                color="#94a3b8"
                style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search agent, phone, address…"
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 36px",
                  borderRadius: 10,
                  border: "1px solid #e2e8f0",
                  fontSize: 13,
                  outline: "none",
                  background: "#f8fafc",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }} className="agent-scroll">
            {loading && !markers.length && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      height: 96,
                      borderRadius: 12,
                      background:
                        "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                    }}
                  />
                ))}
              </div>
            )}

            {!loading && !visibleList.length && (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
                <FiMapPin size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                <p style={{ fontSize: 13, margin: 0 }}>No agents found</p>
              </div>
            )}

            {visibleList.map((m) => {
              const r = m.raw;
              const active = m.idx === activeIndex;
              const dc = designationColor(r.designation);
              return (
                <div
                  key={m.idx}
                  ref={(el) => (listRefs.current[m.idx] = el)}
                  onClick={() => handleSelect(m.idx)}
                  style={{
                    padding: 12,
                    marginBottom: 8,
                    borderRadius: 12,
                    border: active ? "1.5px solid #3b82f6" : "1px solid #eef2f7",
                    background: active ? "#eff6ff" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    boxShadow: active
                      ? "0 4px 14px rgba(59,130,246,0.15)"
                      : "0 1px 3px rgba(0,0,0,0.03)",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = "#fff";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div
                      style={{
                        minWidth: 26,
                        height: 26,
                        borderRadius: 8,
                        background: active
                          ? "linear-gradient(135deg,#ef4444,#dc2626)"
                          : "linear-gradient(135deg,#3b82f6,#2563eb)",
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                      }}
                    >
                      {m.idx + 1}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: "#0f172a",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {r.user_name || "-"}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "2px 7px",
                            borderRadius: 999,
                            background: dc.bg,
                            color: dc.fg,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.designation || "-"}
                        </span>
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: "#475569",
                          marginTop: 3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r.task_name || "-"}
                      </div>

                      {/* Address line (replaces district/municipality) */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 6,
                          marginTop: 6,
                          fontSize: 11.5,
                          color: "#475569",
                          lineHeight: 1.4,
                        }}
                      >
                        <FiNavigation
                          size={12}
                          style={{ marginTop: 2, flexShrink: 0, color: "#3b82f6" }}
                        />
                        <span
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {m.address || "Fetching address…"}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginTop: 6,
                          fontSize: 11,
                          color: "#64748b",
                        }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <FiPhone size={11} /> {r.user_phone || "-"}
                        </span>
                      </div>

                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 10.5,
                          color: "#94a3b8",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>{formatIndianDate(r.created_at)}</span>
                        <span style={{ color: "#16a34a", fontWeight: 600 }}>
                          {timeAgo(r.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              padding: "10px 14px",
              borderTop: "1px solid #f1f5f9",
              fontSize: 11,
              color: "#94a3b8",
              background: "#fafbfc",
            }}
          >
            Showing {visibleList.length} of {markers.length} agents
          </div>
        </aside>

        {/* Map */}
        <main style={{ flex: 1, position: "relative", minWidth: 0 }}>
          {error ? (
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#dc2626",
                fontSize: 14,
              }}
            >
              Error: {error}
            </div>
          ) : (
            <MapView
              markers={markersWithInfo}
              activeIndex={activeIndex}
              onMarkerClick={(idx) => handleSelect(idx)}
            />
          )}

          {/* Legend overlay */}
          {!loading && markers.length > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: 18,
                right: 18,
                background: "rgba(255,255,255,0.96)",
                backdropFilter: "blur(6px)",
                borderRadius: 12,
                padding: "10px 14px",
                boxShadow: "0 6px 20px rgba(15,23,42,0.12)",
                fontSize: 12,
                color: "#334155",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 11, color: "#0f172a", letterSpacing: 0.3 }}>
                LEGEND
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "#2563eb",
                    display: "inline-block",
                  }}
                />
                Ongoing
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "#ef4444",
                    display: "inline-block",
                  }}
                />
                Selected
              </div>
            </div>
          )}

          {/* Loading overlay */}
          {loading && !markers.length && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(248,250,252,0.85)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 12,
                color: "#64748b",
                fontSize: 13,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  border: "3px solid #e2e8f0",
                  borderTopColor: "#3b82f6",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              Loading live locations…
            </div>
          )}
        </main>
      </div>

      {/* Global styles */}
      <style>{`
        @keyframes pulseRing {
          0%   { transform: scale(1);   opacity: 0.7; }
          100% { transform: scale(2.6); opacity: 0; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .agent-scroll::-webkit-scrollbar { width: 6px; }
        .agent-scroll::-webkit-scrollbar-track { background: transparent; }
        .agent-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1; border-radius: 3px;
        }
        .agent-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
};

export default TrackYourAgent;